import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    CircularProgress,
    Avatar,
    IconButton,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';

export default function TestimonialModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({
        name: "",
        message: "",
        date: "",
        thumbnail: null,
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [deleteThumbnail, setDeleteThumbnail] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm((prev) => ({ ...prev, thumbnail: file }));
            setDeleteThumbnail(false);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setForm((prev) => ({ ...prev, thumbnail: null }));
        setImagePreview(null);
        if (row?.thumbnail) {
            setDeleteThumbnail(true);
        }
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            setErrors({});

            const formData = new FormData();
            formData.append('name', form.name);
            formData.append('message', form.message);
            formData.append('date', form.date);
            
            if (form.thumbnail instanceof File) {
                formData.append('thumbnail', form.thumbnail);
            }
            
            if (deleteThumbnail) {
                formData.append('delete_thumbnail', '1');
            }

            if (row?.id) {
                // Use POST route for updates with FormData
                await api.post(`admin/reviews/${row.id}/update`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                await api.post("admin/reviews", formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            }

            onClose(true);
        } catch (e) {
            if (e.errors) {
                setErrors(e.errors);
            } else if (e.response?.data?.errors) {
                setErrors(e.response.data.errors);
            }
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (row) {
            setForm({
                name: row.name || "",
                message: row.message || "",
                date: row.date ? new Date(row.date).toISOString().split('T')[0] : "",
                thumbnail: null,
            });
            // Set image preview if exists
            if (row.thumbnail) {
                const imageUrl = row.thumbnail.startsWith('http') || row.thumbnail.startsWith('/') 
                    ? row.thumbnail 
                    : `/storage/${row.thumbnail}`;
                setImagePreview(imageUrl);
            } else {
                setImagePreview(null);
            }
            setDeleteThumbnail(false);
        } else {
            setForm({
                name: "",
                message: "",
                date: "",
                thumbnail: null,
            });
            setImagePreview(null);
            setDeleteThumbnail(false);
        }
        setErrors({});
        setSaving(false);
    }, [row, open]);

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>{!row ? "Create Testimonial" : "Edit Testimonial"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    {/* Image Upload */}
                    <FormControl error={!!errors.thumbnail?.[0]}>
                        <FormLabel htmlFor="thumbnail">Thumbnail</FormLabel>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            {imagePreview && (
                                <Box sx={{ position: 'relative' }}>
                                    <Avatar
                                        src={imagePreview}
                                        sx={{ width: 80, height: 80 }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        }}
                                        onClick={handleRemoveImage}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            <Button variant="outlined" component="label">
                                {imagePreview ? 'Change Image' : 'Upload Image'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                            </Button>
                        </Box>
                        {errors.thumbnail && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.thumbnail}>
                                {errors.thumbnail[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.name?.[0]}>
                        <FormLabel htmlFor="name">Name *</FormLabel>
                        <OutlinedInput
                            name="name"
                            fullWidth
                            value={form.name}
                            onChange={handleChange}
                        />
                        {errors.name && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.name}>
                                {errors.name[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.message?.[0]}>
                        <FormLabel htmlFor="message">Message *</FormLabel>
                        <OutlinedInput
                            name="message"
                            fullWidth
                            multiline
                            rows={4}
                            value={form.message}
                            onChange={handleChange}
                        />
                        {errors.message && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.message}>
                                {errors.message[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.date?.[0]}>
                        <FormLabel htmlFor="date">Date *</FormLabel>
                        <OutlinedInput
                            name="date"
                            type="date"
                            fullWidth
                            value={form.date}
                            onChange={handleChange}
                        />
                        {errors.date && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.date}>
                                {errors.date[0]}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{ color: '#fff' }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={24} /> : (!row ? "Create" : "Update")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
